import { GenerateProductResponseDto } from '@tiendanube/common';
import { LanguagesType } from 'App/i18n';
import { reduceByLanguage } from './util';
import { ImageGalleryState } from '../../components/ImageGallery/ImageGallery';
import { ProductFormState } from '../../hooks/useProductForm/types';
import { productDefault } from '../constants';

interface DigestAIProductDataParams {
  generatedAIProductData: GenerateProductResponseDto;
  language: LanguagesType;
}

interface DigestAIProductDataReturn {
  product: ProductFormState;
  images: ImageGalleryState[];
}

/**
 * Transforms generated product data into product form state
 * @param generatedAIProductData - Generated product data from AI or other sources
 * @param language - Current user language
 * @returns Transformed product state and images ready for the product form
 */
function digestAIProductData({
  generatedAIProductData,
  language,
}: DigestAIProductDataParams): DigestAIProductDataReturn {
  const images: ImageGalleryState[] =
    generatedAIProductData.images.map((image) => ({
      id: image.id,
      alt: { [language]: '' },
      isLoading: false,
      isError: false,
      isEdited: false,
      src: image.url ?? '',
      mediaType: 'image',
      name: '',
      type: '',
      size: 0,
      mimeType: '',
      videoUrl: '',
    })) ?? [];

  const product: ProductFormState = {
    ...productDefault,
    name: reduceByLanguage(
      generatedAIProductData.name_description,
      'name',
      language,
    ),
    description: reduceByLanguage(
      generatedAIProductData.name_description,
      'description',
      language,
    ),
    seoTitle: reduceByLanguage(
      generatedAIProductData.seo_and_tags.seo,
      'title',
      language,
    ),
    seoDescription: reduceByLanguage(
      generatedAIProductData.seo_and_tags.seo,
      'description',
      language,
    ),
    tags: generatedAIProductData.seo_and_tags.tags,
    brand: generatedAIProductData.seo_and_tags.brand,
    variants: [
      {
        ...productDefault.variants[0],
        ...generatedAIProductData.weight_and_dimensions,
      },
    ],
    categories: generatedAIProductData.categories.map((category) =>
      String(category.category_id),
    ),
    images,
    media: images,
  };

  return {
    product,
    images,
  };
}

export default digestAIProductData;
